/**
 * THE DOCUMENT ATTACHMENT PICKER — `tasks/practice-documents.md` §14.3, §14.4.
 *
 * One component, wherever a practice document can ride along with something the practice is
 * already sending: the manual send from the patient file, a consent request, and — wave 2 — the
 * patient invite, the appointment booking and the WhatsApp chat. It takes a CONTEXT and emits a
 * SELECTION. It holds no HTTP for sending, and it knows nothing about invites, consents,
 * appointments or WhatsApp; the host owns the send.
 *
 * ── THE SHAPE, AND WHY IT CHANGED IN WAVE 1 ──────────────────────────────────────────────────
 *
 * The owner, in their own words:
 *
 *   *"I picture a chip list prefilled with the items linked to appointment sending via trigger, or
 *   linked to an invite etc. Then it has an attach icon that opens a dialog with the group's
 *   documents as a tickable and filterable/searchable list."*
 *
 * So: a CHIP ROW of what is currently attached, each chip removable, plus an ATTACH button that
 * opens `DocumentAttachmentDialogComponent`. Searching and ticking happen in the dialog.
 *
 * **ONE SHAPE EVERYWHERE, and no mode flag.** The manual send dialog is the only host with a
 * plausible claim on an inline list — choosing documents is its whole purpose, so chips cost it
 * one extra click on its primary action. It does not get one. A flag would be two presentations to
 * keep correct for the sake of one click, wave 2 adds three more hosts that would each have to
 * pick a side, and the click buys the send dialog something back: the channels, the physician and
 * the 409 are no longer pushed below a 320px scrolling list.
 *
 * ── WHEN IT IS DRAWN AT ALL ──────────────────────────────────────────────────────────────────
 *
 * The rule, as the owner refined it:
 *
 * | State | Drawn? |
 * |---|---|
 * | Loaded, and the practice has documents | YES — chips if any, an empty row and the button if not |
 * | Loaded, and the practice has NO documents | NO. An attach button for a practice that has never uploaded one is noise on every consent, invite and booking screen they will ever open |
 * | The load FAILED | **YES**, showing the error |
 *
 * That last row is the whole substance of it. A hidden control after a failed load looks identical
 * to a practice with no documents: the user would never know the difference, never retry, and
 * quietly send a consent request without the document that should have ridden along. `isEmpty` and
 * `loadError` were already separated for exactly this reason (§17's correction on the settings
 * tab); wave 1 makes the difference decide whether anything is drawn.
 *
 * **Nothing pre-ticked is NOT empty.** A practice with documents and no configuration for this
 * context still gets the row and the button, because the owner's point is that the user decides
 * per consent, per invite, per appointment.
 *
 * ── CONFIGURATION PRE-TICKS; IT NEVER FORCES ─────────────────────────────────────────────────
 *
 * §14.3, in as many words, and `preTickedIds` in `document-attachment.ts` is the whole rule. The
 * pre-tick is applied ONCE, when the list first arrives, and nothing re-applies it — not a search,
 * not a re-filter, not a second emission. A picker that re-ticked a box the user had just cleared
 * would be a picker that forces.
 *
 * ── THE SENTENCES COME FROM THE SETTINGS TAB'S REGISTER ──────────────────────────────────────
 *
 * `practice-document-copy.ts`, not from here. The screen that SETS `requiresResponse` and the
 * screen that SHOWS it must not be able to disagree about what it means. That file imports nothing
 * from `shared/` — it reads one interface out of `core/services/` — so the dependency is
 * one-directional and no module cycle is possible.
 */

import {
  Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

import { PracticeDocumentDto } from '../../../core/services/practice-document.service';
import {
  requiresResponse as documentRequiresResponse,
  responseModeIcon,
  responseModeSentence,
} from '../../../group/components/practice-document-settings/practice-document-copy';
import {
  DocumentAttachmentChip,
  DocumentAttachmentContext,
  DocumentAttachmentSource,
  DocumentAttachmentTriggerMap,
  preTickedIds,
} from './document-attachment';
import { DocumentAttachmentService } from './document-attachment.service';

export {
  DocumentAttachmentChip,
  DocumentAttachmentContext,
  DocumentAttachmentDialogData,
  DocumentAttachmentDialogResult,
  DocumentAttachmentTriggerMap,
} from './document-attachment';

@Component({
  selector: 'app-document-attachment-picker',
  templateUrl: './document-attachment-picker.component.html',
  styleUrls: ['./document-attachment-picker.component.scss'],
})
export class DocumentAttachmentPickerComponent implements OnInit, OnChanges, OnDestroy {

  /** Which send this is part of. Decides what is pre-ticked and what is offerable, and nothing else. */
  @Input() context: DocumentAttachmentContext = 'manual';

  /**
   * The patient this send is for.
   *
   * The picker makes no request per patient and shows nothing about them — but the practice a
   * patient belongs to decides which documents may be attached, so a change of patient re-reads
   * the list rather than leaving another practice's blanks on screen.
   */
  @Input() patientId?: string | null;

  /**
   * The PATIENT'S practice. Omit it and the service falls back to the practice the user has
   * selected — right for a settings-side host, wrong for a patient in another practice, which is
   * why every patient-side host passes it.
   */
  @Input() groupId?: string | null;

  /**
   * `appointment` only: the type whose ACTIVE links pre-tick this row (§24.2).
   *
   * Absent is not an error — it means no triggers and nothing pre-ticked, which is what a booking
   * screen shows before a type has been chosen.
   */
  @Input() appointmentTypeId?: string | null;

  /** The heading above the chip row. A host says what its own send is called. */
  @Input() label = 'Documents to send';

  /** The ticked document ids. Two-way: `[(selection)]`. */
  @Input() selection: string[] = [];
  @Output() selectionChange = new EventEmitter<string[]>();

  /**
   * The rows behind those ids, for a host that has to NAME what it is sending — a toast, a
   * refusal, a confirmation. Emitted alongside `selectionChange` so a host never has to hold a
   * reference to this component to turn an id back into a document.
   */
  @Output() selectedDocumentsChange = new EventEmitter<PracticeDocumentDto[]>();

  /** `null` while loading: "we have not looked yet" is not "there are none". */
  documents: PracticeDocumentDto[] | null = null;
  triggers: DocumentAttachmentTriggerMap = {};
  loadError: string | null = null;
  triggerError: string | null = null;

  /** Set the moment the list arrives. Nothing re-applies the configuration after it. */
  private preTicked = false;

  private destroy$ = new Subject<void>();

  constructor(private attachments: DocumentAttachmentService) { }

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A change of practice, patient or appointment type means a different set of blanks or a
    // different set of triggers; a change of `selection` from the host is just the host holding
    // the value we emitted.
    const reload = ['groupId', 'patientId', 'appointmentTypeId']
      .some((input) => changes[input] && !changes[input].firstChange);
    if (reload) this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * The list
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  private load(): void {
    this.documents = null;
    this.loadError = null;
    this.triggerError = null;
    this.triggers = {};
    this.preTicked = false;

    this.attachments.load({
      context: this.context,
      groupId: this.groupId,
      appointmentTypeId: this.appointmentTypeId,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((source) => {
        this.documents = source.documents;
        this.triggers = source.triggers;
        this.loadError = source.loadError;
        this.triggerError = source.triggerError;
        this.applyConfiguration(source);
      });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Configuration pre-ticks; it never forces
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  private applyConfiguration(source: DocumentAttachmentSource): void {
    if (this.preTicked) return;
    this.preTicked = true;

    // A host that arrived with a selection has already chosen — a user reopening a dialog, say.
    // Overwriting it with the configuration would silently discard their choice.
    if (this.selection && this.selection.length) {
      this.emit(this.selection);
      return;
    }

    this.emit(preTickedIds(source, this.context));
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * The chips
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * WHAT IS CURRENTLY ATTACHED, in the order the practice's list gives them.
   *
   * Built from the loaded rows, so an id in `selection` that names no known document draws no
   * chip. That is deliberate: a chip reading "unknown document" would be a chip a user cannot act
   * on, and the id is still in the selection and still goes to the host either way.
   */
  get chips(): DocumentAttachmentChip[] {
    return this.selectedDocuments.map((document) => ({
      id: document.id,
      name: document.name,
      trigger: this.triggers[document.id] || null,
      requiresResponse: documentRequiresResponse(document),
      responseSentence: responseModeSentence(document),
      responseIcon: responseModeIcon(document),
    }));
  }

  /** A chip's × . The one thing a user can do to the row without opening the dialog. */
  remove(documentId: string): void {
    this.emit((this.selection || []).filter((id) => id !== documentId));
  }

  /**
   * KEPT PUBLIC. The two existing hosts' suites drive selection through it, and so does anything
   * that needs to flip one document without opening a dialog. It is the same rule the dialog
   * applies, on the host's array rather than on a copy.
   */
  toggle(document: PracticeDocumentDto): void {
    const current = this.selection || [];
    this.emit(this.isSelected(document)
      ? current.filter((id) => id !== document.id)
      : current.concat([document.id]));
  }

  isSelected(document: PracticeDocumentDto): boolean {
    return (this.selection || []).indexOf(document.id) !== -1;
  }

  clearSelection(): void {
    this.emit([]);
  }

  private emit(selection: string[]): void {
    this.selection = selection;
    this.selectionChange.emit(selection);
    this.selectedDocumentsChange.emit(this.selectedDocuments);
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * The attach dialog
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * PUBLIC, so a host can open it from its own control — a toolbar button, a menu item — without
   * the user having to find the one inside this row.
   *
   * A cancelled dialog closes with `undefined` and changes nothing. Reading that as an empty
   * selection would untick everything every time somebody pressed Escape.
   */
  openAttachDialog(): void {
    if (!this.canAttach) return;

    this.attachments.open({
      context: this.context,
      documents: this.documents || [],
      selection: (this.selection || []).slice(),
      triggers: this.triggers,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (!result) return;
        this.emit(result.selection);
      });
  }

  /**
   * Nothing to choose from that we KNOW of. Disabled while loading, and after a failure — where
   * offering a dialog built from an empty list would claim the practice has no documents, which is
   * the very thing `loadError` exists to avoid saying.
   */
  get canAttach(): boolean {
    return !this.isLoading && !this.loadError;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * What is on screen
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  get selectedDocuments(): PracticeDocumentDto[] {
    const ids = this.selection || [];
    return (this.documents || []).filter((document) => ids.indexOf(document.id) !== -1);
  }

  get selectedCount(): number {
    return this.selectedDocuments.length;
  }

  /*
   * `consequence` WAS HERE and is gone. The chip row drew it under the chips, where it restated
   * what the chips already said; the dialog draws it, where it is the only thing that describes
   * the MIX rather than one document. A getter nothing renders is the read-but-never-used shape
   * this feature has now removed three times, and the dialog computes its own.
   */

  get isLoading(): boolean {
    return this.documents === null;
  }

  /**
   * A practice with nothing to attach — and the ONE state in which the whole control is hidden.
   *
   * NOT the same as a load that failed, which is drawn, with the error. See the class header.
   */
  get isEmpty(): boolean {
    return !this.isLoading && !this.loadError && (this.documents || []).length === 0;
  }

  /**
   * THE ONE GETTER THE TEMPLATE'S OUTER `*ngIf` READS.
   *
   * Hidden while loading as well as when empty, so a practice with no documents never gets a row
   * that appears and then vanishes.
   *
   * **A FAILED LOAD IS DRAWN, and this line is not where that is decided** — `isEmpty` is, by its
   * own `!this.loadError` clause. This getter carried an explicit `if (this.loadError) return
   * true` until a mutation run proved no check could kill it: it was equivalent to the code
   * around it, so it read as the mechanism while being dead weight (lessons.md §25, §26). The rule
   * lives in ONE place now, and mutating THAT place takes the suite red.
   */
  get isVisible(): boolean {
    return !this.isLoading && !this.isEmpty;
  }

  /** Nothing attached yet. The quiet row that still offers the button. */
  get isUnattached(): boolean {
    return this.selectedCount === 0;
  }

  trackByChip(_index: number, chip: DocumentAttachmentChip): string {
    return chip.id;
  }
}
